import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import AppConstants from "../../../utils/appConstants";
import { API_ROUTES, getAppUrls, postEncryptedJson } from "../../../utils/apiClient";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { useBrandLogo } from "../../../utils/brandLogo";
import { useToast } from "../../../contexts/ToastContext";
import {
  INSTALL_AVAILABLE_EVENT,
  INSTALLED_EVENT,
  isStandaloneDisplay,
  promptPwaInstall,
} from "../../../utils/pwaInstall";
import "../../css/supportPages.css";

export default function SupportAboutUs() {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const location = useLocation();
  const brandLogo = useBrandLogo();
  const { warning } = useToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [about, setAbout] = useState({
    email: "",
    phone: "",
    website: "",
    location: "",
    facebook: "",
    instagram: "",
    whatsapp: "",
    tiktok: "",
    androidAppUrl: "",
    iosAppUrl: "",
  });
  const [shortcutInstalled, setShortcutInstalled] = useState(() => isStandaloneDisplay());
  const [shortcutInstalling, setShortcutInstalling] = useState(false);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAbout = async () => {
      setLoading(true);
      setError("");
      try {
        const [aboutResponse, appUrlsResponse] = await Promise.allSettled([
          postEncryptedJson(API_ROUTES.GET_CONTACT_US_INFO, {}),
          getAppUrls(),
        ]);

        const data =
          (aboutResponse.status === "fulfilled" &&
            (aboutResponse.value?.decryptedData || aboutResponse.value?.data)) ||
          {};
        const code = Number(data?.CODE);

        const appUrls =
          (appUrlsResponse.status === "fulfilled" && appUrlsResponse.value) || {};
        const androidAppUrl = appUrls?.android || "";
        const iosAppUrl = appUrls?.ios || "";

        if (code !== AppConstants.responseOk) {
          if (!cancelled) setError(data?.MESSAGE || "Failed to load about information.");
          return;
        }

        if (!cancelled) {
          setAbout({
            email: data?.EMAIL || "",
            phone: data?.PHONE || data?.phone || data?.TEL || "",
            website: data?.WEBSITE || "",
            location: data?.LOCATION || "",
            facebook: data?.FACEBOOK || "",
            instagram: data?.INSTAGRAM || "",
            whatsapp: data?.WHATSAPP || "",
            tiktok: data?.TIKTOK || "",
            androidAppUrl,
            iosAppUrl,
          });
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load about information.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAbout();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onInstalled = () => setShortcutInstalled(true);
    const onAvailable = () => setShortcutInstalled(isStandaloneDisplay());
    window.addEventListener(INSTALLED_EVENT, onInstalled);
    window.addEventListener(INSTALL_AVAILABLE_EVENT, onAvailable);
    return () => {
      window.removeEventListener(INSTALLED_EVENT, onInstalled);
      window.removeEventListener(INSTALL_AVAILABLE_EVENT, onAvailable);
    };
  }, []);

  const mapsUrl = useMemo(() => {
    if (!about.location?.trim()) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(about.location.trim())}`;
  }, [about.location]);

  const mapEmbedUrl = useMemo(() => {
    if (!about.location?.trim()) return null;
    return `https://www.google.com/maps?q=${encodeURIComponent(about.location.trim())}&output=embed`;
  }, [about.location]);

  const openLiveChat = () => {
    openSupportChatOnCurrentPage(navigate, location.pathname, location.search);
  };

  const installShortcut = useCallback(async () => {
    if (shortcutInstalled || shortcutInstalling) return;
    setShortcutInstalling(true);
    try {
      const result = await promptPwaInstall();
      if (result.ok || result.reason === "installed") {
        setShortcutInstalled(true);
        return;
      }
      if (result.reason === "ios") {
        warning(
          t("pwa_ios_title", { defaultValue: "Install MFG Invest" }),
          t("pwa_ios_message", {
            defaultValue: "Tap Share, then Add to Home Screen for quicker access.",
          }),
          8000
        );
        return;
      }
      if (result.reason === "unavailable") {
        warning(
          t("pwa_install_title", { defaultValue: "Install MFG Invest" }),
          t("pwa_install_message", {
            defaultValue: "Add MFG Invest to your home screen for quicker access.",
          }),
          8000
        );
      }
    } finally {
      setShortcutInstalling(false);
    }
  }, [
    shortcutInstalled,
    shortcutInstalling,
    t,
    warning,
  ]);

  const renderShortcutSocialCell = () => {
    const label = shortcutInstalled
      ? t("about_social_shortcut_installed", { defaultValue: "Installed" })
      : t("about_social_shortcut", { defaultValue: "Shortcut" });
    const inner = (
      <>
        <span className="about-us-social-icon about-us-social-icon--logo">
          <img src={brandLogo} alt="" className="about-us-social-logo-img" />
        </span>
        <span className="about-us-social-label">{label}</span>
      </>
    );
    if (shortcutInstalled) {
      return (
        <div className="about-us-social-cell about-us-social-cell--disabled" aria-disabled="true">
          {inner}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`about-us-social-cell${shortcutInstalling ? " about-us-social-cell--disabled" : ""}`}
        onClick={installShortcut}
        disabled={shortcutInstalling}
        aria-label={label}
      >
        {inner}
      </button>
    );
  };

  const normalizeWebsiteUrl = (url) => {
    if (!url || !String(url).trim()) return "";
    const s = String(url).trim();
    if (/^https?:\/\//i.test(s)) return s;
    return `https://${s}`;
  };

  const renderSocialCell = (href, iconClass, label, brandClass) => {
    const inner = (
      <>
        <span className={`about-us-social-icon ${brandClass}`}>
          <i className={iconClass} aria-hidden />
        </span>
        <span className="about-us-social-label">{label}</span>
      </>
    );
    if (href) {
      return (
        <a
          className="about-us-social-cell"
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          {inner}
        </a>
      );
    }
    return (
      <div className="about-us-social-cell about-us-social-cell--disabled" aria-disabled="true">
        {inner}
      </div>
    );
  };

  const renderWebsiteSocialCell = (href) => {
    const inner = (
      <>
        <span className="about-us-social-icon about-us-social-icon--logo">
          <img src={brandLogo} alt="MFG Invest" className="about-us-social-logo-img" />
        </span>
        <span className="about-us-social-label">{t("website")}</span>
      </>
    );
    if (href) {
      return (
        <a className="about-us-social-cell" href={href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      );
    }
    return (
      <div className="about-us-social-cell about-us-social-cell--disabled" aria-disabled="true">
        {inner}
      </div>
    );
  };

  const renderAppUrlSocialCell = (href, label, iconClass, brandClass = "about-us-social-icon--app") => {
    const inner = (
      <>
        <span className={`about-us-social-icon ${brandClass}`}>
          <i className={iconClass} aria-hidden />
        </span>
        <span className="about-us-social-label">{label}</span>
      </>
    );
    if (href) {
      return (
        <a className="about-us-social-cell" href={href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      );
    }
    return (
      <div className="about-us-social-cell about-us-social-cell--disabled" aria-disabled="true">
        {inner}
      </div>
    );
  };

  return (
    <div className="support-page support-page--about">
      <Sidebar user={user} />
      <div className="support-content support-content--about">
        {error && !loading ? (
          <div className="support-center-message support-error">{error}</div>
        ) : (
          <div className="about-us">
            <h1 className="about-us-title">{t("about_us")}</h1>

            <div className="about-us-top-grid">
              <section className="about-us-card" aria-labelledby="about-get-in-touch">
                <h2 id="about-get-in-touch" className="about-us-card-title">
                  {t("about_get_in_touch")}
                </h2>
                <ul className="about-us-touch-list">
                  <li className="about-us-touch-row">
                    <span className="about-us-icon-square" aria-hidden>
                      <i className="fa-regular fa-envelope" />
                    </span>
                    <div className="about-us-touch-texts">
                      <span className="about-us-touch-heading">{t("about_email_support")}</span>
                      {loading ? (
                        <span className="about-us-skeleton about-us-skeleton--value" aria-hidden />
                      ) : about.email ? (
                        <a className="about-us-touch-value" href={`mailto:${about.email}`}>
                          {about.email}
                        </a>
                      ) : (
                        <span className="about-us-touch-value about-us-touch-value--muted">—</span>
                      )}
                      <span className="about-us-touch-sub">{t("about_email_support_sub")}</span>
                    </div>
                  </li>
                  <li className="about-us-touch-row">
                    <span className="about-us-icon-square" aria-hidden>
                      <i className="fa-solid fa-phone" />
                    </span>
                    <div className="about-us-touch-texts">
                      <span className="about-us-touch-heading">{t("about_phone_support")}</span>
                      {loading ? (
                        <span className="about-us-skeleton about-us-skeleton--value" aria-hidden />
                      ) : about.phone ? (
                        <a className="about-us-touch-value" href={`tel:${about.phone.replace(/\s/g, "")}`}>
                          {about.phone}
                        </a>
                      ) : (
                        <span className="about-us-touch-value about-us-touch-value--muted">—</span>
                      )}
                      <span className="about-us-touch-sub">{t("about_phone_support_sub")}</span>
                    </div>
                  </li>
                  <li className="about-us-touch-row">
                    <span className="about-us-icon-square" aria-hidden>
                      <i className="fa-regular fa-comments" />
                    </span>
                    <div className="about-us-touch-texts">
                      <span className="about-us-touch-heading">{t("about_live_chat")}</span>
                      <button type="button" className="about-us-touch-value about-us-touch-link" onClick={openLiveChat}>
                        {t("about_live_chat_value")}
                      </button>
                      <span className="about-us-touch-sub">{t("about_live_chat_sub")}</span>
                    </div>
                  </li>
                </ul>
              </section>

              <section className="about-us-card" aria-labelledby="about-follow-us">
                <h2 id="about-follow-us" className="about-us-card-title">
                  {t("about_follow_us")}
                </h2>
                <p className="about-us-card-intro">{t("about_follow_us_intro")}</p>
                <div className="about-us-social-grid about-us-social-grid--triple">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                      <div key={`social-skeleton-${index}`} className="about-us-social-cell about-us-social-cell--disabled">
                        <span className="about-us-skeleton about-us-skeleton--icon" aria-hidden />
                        <span className="about-us-skeleton about-us-skeleton--label" aria-hidden />
                      </div>
                    ))
                  ) : (
                    <>
                      {renderAppUrlSocialCell(
                        normalizeWebsiteUrl(about.androidAppUrl),
                        t("about_social_app_android", { defaultValue: "Android App" }),
                        "fa-brands fa-google-play",
                        "about-us-social-icon--android"
                      )}
                      {renderAppUrlSocialCell(
                        normalizeWebsiteUrl(about.iosAppUrl),
                        t("about_social_app_ios", { defaultValue: "iOS App" }),
                        "fa-brands fa-apple",
                        "about-us-social-icon--ios"
                      )}
                      {renderWebsiteSocialCell(normalizeWebsiteUrl(about.website || about.androidAppUrl || about.iosAppUrl))}
                      {renderSocialCell(
                        about.facebook,
                        "fa-brands fa-facebook-f",
                        t("about_social_facebook"),
                        "about-us-social-icon--facebook"
                      )}
                      {renderSocialCell(
                        about.instagram,
                        "fa-brands fa-instagram",
                        t("about_social_instagram"),
                        "about-us-social-icon--instagram"
                      )}
                      {renderShortcutSocialCell()}
                    </>
                  )}
                </div>
              </section>
            </div>

            <section className="about-us-card about-us-card--hq" aria-labelledby="about-hq">
              <h2 id="about-hq" className="about-us-card-title">
                {t("about_headquarters")}
              </h2>
              <div className="about-us-hq-body">
                <div className="about-us-hq-block">
                  <span className="about-us-icon-square" aria-hidden>
                    <i className="fa-solid fa-location-dot" />
                  </span>
                  <div className="about-us-hq-block-text">
                    <span className="about-us-hq-label">{t("about_address_label")}</span>
                    {loading ? (
                      <span className="about-us-skeleton about-us-skeleton--address" aria-hidden />
                    ) : (
                      <p className="about-us-hq-value">
                        {about.location?.trim() ? about.location : "—"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="about-us-hq-block">
                  <span className="about-us-icon-square" aria-hidden>
                    <i className="fa-regular fa-clock" />
                  </span>
                  <div className="about-us-hq-block-text">
                    <span className="about-us-hq-label">{t("about_office_hours")}</span>
                    <p className="about-us-hq-value about-us-hq-hours">{t("about_office_hours_sentence")}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
